import type {
  SupplyEvent,
  SupplyEventKind,
  SupplyHourPoint,
  SupplyPreviousForecast,
  SupplyTimelineResponse,
} from '@gozoki/api-types'
import { capitalizeFirstLetter, mapGroupBy } from '@gozoki/tools'
import { DateTime } from 'luxon'
import { type JSX, type ReactNode, useCallback, useMemo } from 'react'
import { BandedTimelineChart } from '../charts/banded-timeline-chart'
import type {
  TimelineBand,
  TimelineBarSegment,
  TimelineCardContent,
  TimelineCardRow,
  TimelineMarker,
  TimelineSeries,
} from '../charts/banded-timeline-types'
import {
  formatInt,
  formatPercent,
  previousForecastValue,
  FORECAST_DISCLAIMER,
  PARIS_ZONE,
  SUPPLY_COLORS,
  SUPPLY_EVENT_LABELS,
  SUPPLY_KPI_LABELS,
  SUPPLY_LEGEND_COLORS,
  STOCKOUT_CAUSE_LABELS,
} from './supply-labels'
import { BreakageIcon, DeliveryMarker, OrderMarker, StockoutIcon } from './supply-icons'

const SLOT_WIDTH = 18
const BAR_GUTTER_WIDTH = 1
const BAND_RADIUS = 16
const TICK_HOURS_STEP = 4
const STOCK_HIGHLIGHT_COLOR = '#68885f'
const FORECAST_STROKE_WIDTH = 2.5

const EVENT_MARKER_COLORS: Record<SupplyEventKind, string> = {
  breakage: SUPPLY_COLORS.errorText,
  delivery: SUPPLY_LEGEND_COLORS.deliveryMark,
  production_order: SUPPLY_LEGEND_COLORS.orders,
  stockout: SUPPLY_COLORS.stockoutMark,
}

// Only the loss and stockout markers drop a line: they point at a precise moment worth locating
// on the bars, unlike deliveries and orders which just sit on the marker row.
const DROP_LINE_KINDS = new Set<SupplyEventKind>(['stockout', 'breakage'])

function renderEventIcon(kind: SupplyEventKind): ReactNode {
  switch (kind) {
    case 'delivery': {
      return <DeliveryMarker size={14} />
    }
    case 'production_order': {
      return <OrderMarker size={14} />
    }
    // Losses and stockouts are the markers operators look for, so they read larger than the rest.
    case 'stockout': {
      return <StockoutIcon size={20} />
    }
    case 'breakage': {
      return <BreakageIcon size={20} />
    }
  }
}

const BAR_WIDTH_RATIO = (SLOT_WIDTH - BAR_GUTTER_WIDTH) / SLOT_WIDTH
const BAR_RADIUS: [number, number, number, number] = [6, 6, 0, 0]
const PREVIOUS_FORECAST_STROKE_WIDTH = 4
const PREVIOUS_FORECAST_KEY = 'previousForecast'
const PREVIOUS_FORECAST_LABEL = 'Prévision il y a 24 h'
const PREVIOUS_FORECAST_ROW_LABEL = 'Prév. −24 h'

function buildPreviousForecastSeries(
  hours: SupplyHourPoint[],
  previousForecast: SupplyPreviousForecast | null,
): TimelineSeries[] {
  if (!previousForecast) return []
  return [
    {
      key: PREVIOUS_FORECAST_KEY,
      label: PREVIOUS_FORECAST_LABEL,
      type: 'line',
      color: SUPPLY_COLORS.previousForecast,
      strokeWidth: PREVIOUS_FORECAST_STROKE_WIDTH,
      outsideYDomain: true,
      data: hours.map((hour, index) => ({
        t: hour.t,
        value: previousForecastValue(previousForecast.sales, index),
      })),
    },
  ]
}

function buildSeries(
  hours: SupplyHourPoint[],
  previousForecast: SupplyPreviousForecast | null,
): TimelineSeries[] {
  return [
    {
      key: 'stock',
      label: SUPPLY_KPI_LABELS.stock,
      type: 'bar',
      color: SUPPLY_COLORS.stock,
      opacity: 0.7,
      radius: BAR_RADIUS,
      barWidthRatio: BAR_WIDTH_RATIO,
      highlightColor: STOCK_HIGHLIGHT_COLOR,
      data: hours.map((hour) => ({ t: hour.t, value: hour.stock })),
    },
    // Painted before the sales bar so the solid one covers its base: only the missed sales show.
    {
      key: 'correctedSales',
      label: SUPPLY_KPI_LABELS.correctedSales,
      type: 'bar',
      color: SUPPLY_LEGEND_COLORS.correctedSales,
      radius: BAR_RADIUS,
      barWidthRatio: BAR_WIDTH_RATIO,
      data: hours.map(({ t, sales, correctedSales }) => {
        if (sales === null || correctedSales === null) return { t, value: null }
        const missed = Math.round(correctedSales - sales)
        return { t, value: missed > 0 ? sales + missed : null }
      }),
    },
    {
      key: 'sales',
      label: SUPPLY_KPI_LABELS.sales,
      type: 'bar',
      color: SUPPLY_COLORS.sales,
      radius: BAR_RADIUS,
      barWidthRatio: BAR_WIDTH_RATIO,
      data: hours.map((hour) => ({ t: hour.t, value: hour.sales })),
    },
    // Painted before the current forecast, which stays the most legible curve of the chart.
    ...buildPreviousForecastSeries(hours, previousForecast),
    {
      key: 'forecastSales',
      label: `Prévision ventes (${FORECAST_DISCLAIMER})`,
      type: 'line',
      color: SUPPLY_COLORS.forecast,
      dashed: true,
      strokeWidth: FORECAST_STROKE_WIDTH,
      data: hours.map((hour) => ({ t: hour.t, value: Math.round(hour.forecastSales) })),
    },
    {
      key: 'forecastStock',
      label: `Stock prévu (${FORECAST_DISCLAIMER})`,
      type: 'bar',
      color: SUPPLY_COLORS.stock,
      opacity: 0.6,
      radius: BAR_RADIUS,
      barWidthRatio: BAR_WIDTH_RATIO,
      highlightColor: STOCK_HIGHLIGHT_COLOR,
      data: hours.map((hour) => ({ t: hour.t, value: hour.forecastStock })),
    },
  ]
}

const MAX_MARKER_PRODUCTS = 5

const DELIVERY_MARKER_ARIA_LABEL = "Voir l'inventaire de la livraison"

const MARKER_UNITS: Partial<Record<SupplyEventKind, string>> = {
  breakage: 'unité',
  delivery: 'article',
  production_order: 'unité',
  stockout: 'produit',
}

// Losses come from the night inventory: its hour says nothing useful, unlike a stockout's.
function markerTitle(event: SupplyEvent): ReactNode {
  const kind = SUPPLY_EVENT_LABELS[event.kind]
  if (event.kind !== 'breakage') {
    const hour = DateTime.fromISO(event.t, { zone: PARIS_ZONE }).toFormat('HH:mm')
    return <span>{`${kind} - ${hour}`}</span>
  }
  return (
    <>
      <span>{`${kind} -`}</span>
      <span style={{ color: SUPPLY_COLORS.errorText }}>{formatInt(event.count)}</span>
      {event.rate === null || event.rate === undefined ? null : (
        <span className="text-xs leading-[14px]" style={{ color: SUPPLY_COLORS.errorText }}>
          ({formatPercent(event.rate)})
        </span>
      )}
    </>
  )
}

// A stockout counts one row per product, so its names carry the whole story and a count of 1 next
// to each would only be noise.
function productRows(event: SupplyEvent): TimelineCardRow[] {
  const products = event.products ?? []
  const rows = products.slice(0, MAX_MARKER_PRODUCTS).map((product) => ({
    label: product.label,
    value: event.kind === 'stockout' ? '' : formatInt(product.count),
  }))
  const hidden = products.length - rows.length
  if (hidden > 0) rows.push({ label: `et ${hidden} autre${hidden > 1 ? 's' : ''}`, value: '' })
  return rows
}

function markerDetails(event: SupplyEvent): TimelineCardRow[] {
  const rows: TimelineCardRow[] = []
  if (event.kind === 'breakage') {
    if (event.fifoScore !== null && event.fifoScore !== undefined) {
      rows.push({ label: 'Score FIFO', value: formatInt(event.fifoScore) })
    }
  } else if (event.kind === 'stockout') {
    if (event.cause) rows.push({ label: 'En cause', value: STOCKOUT_CAUSE_LABELS[event.cause] })
  } else {
    const unit = MARKER_UNITS[event.kind]
    if (unit) {
      rows.push({ label: `${unit}${event.count > 1 ? 's' : ''}`, value: formatInt(event.count) })
    }
  }
  return [...rows, ...productRows(event)]
}

// Markers sharing an hour stack on top of each other, and a loss shares its hour with the
// stockouts it causes: it paints last so it is the one left visible.
function buildMarkers(
  events: SupplyEvent[],
  onDeliveryClick?: (event: SupplyEvent) => void,
): TimelineMarker[] {
  const ordered = [...events].sort(
    (a, b) => Number(a.kind === 'breakage') - Number(b.kind === 'breakage'),
  )
  return ordered.map((event) => {
    const hasDropLine = DROP_LINE_KINDS.has(event.kind)
    const clickable = onDeliveryClick !== undefined && event.kind === 'delivery'
    return {
      id: event.id,
      t: event.t,
      icon: renderEventIcon(event.kind),
      color: EVENT_MARKER_COLORS[event.kind],
      dropLine: hasDropLine,
      filled: hasDropLine,
      details: markerDetails(event),
      title: markerTitle(event),
      ariaLabel: clickable ? DELIVERY_MARKER_ARIA_LABEL : undefined,
      onClick: clickable ? () => onDeliveryClick(event) : undefined,
    }
  })
}

// The broken units were part of that hour's stock, so they colour its top rather than adding to it.
function buildBarSegments(events: SupplyEvent[]): TimelineBarSegment[] {
  return events.flatMap((event) =>
    event.kind === 'breakage'
      ? [
          {
            color: SUPPLY_COLORS.breakage,
            seriesKey: 'stock',
            t: event.t,
            topRadius: BAR_RADIUS[0],
            value: event.count,
          },
        ]
      : [],
  )
}

function dayKey(iso: string): string {
  return DateTime.fromISO(iso, { zone: PARIS_ZONE }).toISODate()!
}

const hoursByDay = (hours: SupplyHourPoint[]): Map<string, SupplyHourPoint[]> =>
  mapGroupBy(hours, (hour) => dayKey(hour.t))

function sumForecastSales(dayHours: SupplyHourPoint[]): number {
  return dayHours.reduce((total, hour) => total + hour.forecastSales, 0)
}

function dayHeaderCard(start: DateTime, dayHours: SupplyHourPoint[]): TimelineCardContent {
  const sales = dayHours.reduce((total, hour) => total + (hour.sales ?? 0), 0)
  // The backend windows whole days, so the last slot is the 23h one: the day's closing stock.
  const closing = dayHours.at(-1)
  return {
    title: capitalizeFirstLetter(start.setLocale('fr').toFormat('cccc dd/MM')),
    rows: [
      { label: SUPPLY_KPI_LABELS.vpj, value: formatInt(sumForecastSales(dayHours)) },
      {
        label: SUPPLY_KPI_LABELS.stock,
        value: formatInt(closing?.stock ?? closing?.forecastStock),
      },
      { label: 'Ventes', value: formatInt(sales) },
    ],
  }
}

function buildBands(
  hours: SupplyHourPoint[],
  now: string,
  variant: SupplyChartVariant,
): TimelineBand[] {
  const today = dayKey(now)
  const isStore = variant === 'store'

  return [...hoursByDay(hours)]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, dayHours]) => {
      const start = DateTime.fromISO(day, { zone: PARIS_ZONE }).startOf('day')
      const weekday = start.setLocale('fr').toFormat('ccc').toUpperCase()
      return {
        key: day,
        label: isStore ? (
          <span className="font-semibold">{weekday}</span>
        ) : (
          <>
            <span className="font-semibold">{weekday}</span>{' '}
            <span className="font-normal opacity-60">{start.toFormat('dd/MM')}</span>
          </>
        ),
        from: start.toISO()!,
        to: start.plus({ days: 1 }).toISO()!,
        dimmed: day > today,
        dimFrom: day === today ? now : undefined,
        highlighted: isStore && day === today,
        headerCard: dayHeaderCard(start, dayHours),
      }
    })
}

function formatTick(t: string): string {
  return DateTime.fromISO(t, { zone: PARIS_ZONE }).toFormat('HH:mm')
}

function isHourTick(t: string): boolean {
  const hour = DateTime.fromISO(t, { zone: PARIS_ZONE }).hour
  return hour > 0 && hour % TICK_HOURS_STEP === 0
}

// VPJ is the day's forecast sales volume, the same sum the backend KPI computes for today.
function buildForecastByDay(hours: SupplyHourPoint[]): Map<string, number> {
  return new Map([...hoursByDay(hours)].map(([day, dayHours]) => [day, sumForecastSales(dayHours)]))
}

// Pinned halfway up the axis, so sales read on its lower half and stock on its upper half.
function computeSalesPeak(hours: SupplyHourPoint[]): number {
  return hours.reduce((peak, hour) => Math.max(peak, hour.sales ?? 0, hour.forecastSales), 0)
}

export type SupplyChartVariant = 'default' | 'store'

const STORE_DIMMED_BAND_OPACITY = 0.85

interface SupplyChartProps {
  data: SupplyTimelineResponse
  showAlerts: boolean
  showPreviousForecast?: boolean
  variant?: SupplyChartVariant
  height?: number
  onDeliveryClick?: (event: SupplyEvent) => void
}

export function SupplyChart({
  data,
  showAlerts,
  showPreviousForecast = false,
  variant = 'default',
  height,
  onDeliveryClick,
}: SupplyChartProps): JSX.Element {
  const isStore = variant === 'store'
  const previousForecast = showPreviousForecast ? data.previousForecast : null
  const series = useMemo(
    () => buildSeries(data.hours, previousForecast),
    [data.hours, previousForecast],
  )
  const markers = useMemo(
    () => (showAlerts ? buildMarkers(data.events, onDeliveryClick) : []),
    [data.events, showAlerts, onDeliveryClick],
  )
  const barSegments = useMemo(() => buildBarSegments(data.events), [data.events])
  const bands = useMemo(
    () => buildBands(data.hours, data.window.now, variant),
    [data.hours, data.window.now, variant],
  )
  const forecastByDay = useMemo(() => buildForecastByDay(data.hours), [data.hours])
  const salesPeak = useMemo(() => computeSalesPeak(data.hours), [data.hours])

  const slotCard = useCallback(
    (t: string, values: Record<string, number | null>): TimelineCardContent => {
      const vpj = forecastByDay.get(dayKey(t)) ?? 0
      const previousValue = values[PREVIOUS_FORECAST_KEY]
      const rows: TimelineCardRow[] = [
        { label: SUPPLY_KPI_LABELS.vpj, value: formatInt(vpj) },
        { label: SUPPLY_KPI_LABELS.vph, value: formatInt(values.forecastSales) },
        ...(previousValue === null || previousValue === undefined
          ? []
          : [{ label: PREVIOUS_FORECAST_ROW_LABEL, value: formatInt(previousValue) }]),
        {
          label: SUPPLY_KPI_LABELS.stock,
          value: formatInt(values.stock ?? values.forecastStock),
        },
        { label: 'Ventes', value: formatInt(values.sales) },
      ]
      return {
        title: DateTime.fromISO(t, { zone: PARIS_ZONE }).toFormat('HH:mm'),
        rows,
      }
    },
    [forecastByDay],
  )

  return (
    <div className="-mb-4">
      <BandedTimelineChart
        series={series}
        markers={markers}
        barSegments={barSegments}
        bands={bands}
        nowLine={data.window.now}
        yScaleMidpoint={salesPeak}
        minSlotWidth={SLOT_WIDTH}
        slotCard={slotCard}
        bandGradientFrom={SUPPLY_COLORS.bandGradientFrom}
        bandGradientTo={SUPPLY_COLORS.bandGradientTo}
        bandRadius={BAND_RADIUS}
        dimVeilColor={SUPPLY_COLORS.pageBg}
        headerTextColor={SUPPLY_COLORS.darkText}
        axisTickColor={SUPPLY_COLORS.darkText}
        barLayout="overlap"
        tickFilter={isHourTick}
        tickFormatter={formatTick}
        dimmedBandOpacity={isStore ? STORE_DIMMED_BAND_OPACITY : undefined}
        highlightBandColor={isStore ? '#BAD7A2' : undefined}
        highlightTextColor={isStore ? SUPPLY_COLORS.darkText : undefined}
        height={height}
        fitAllBands={isStore}
      />
    </div>
  )
}
